import { styled } from '@mui/material/styles';
import { shouldForwardProp as muiShouldOverrideProp } from '@mui/system/createStyled';
import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { checkboxClasses } from '@mui/material/Checkbox';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { badgeClasses } from '@mui/material/Badge';

const propsForStylingOnly = [
  'selected',
  'nested',
  'lastSelected',
  'group',
  'focused',
  'disabled',
  'withCheckbox'
];

export const SelectOptionRoot = styled('div', {
  shouldForwardProp: prop =>
    !propsForStylingOnly.includes(prop) && muiShouldOverrideProp(prop),
  name: 'AFSelectOption'
})(({ theme, ...props }) => ({
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  position: 'relative',
  width: '100%',
  boxSizing: 'border-box',
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },
  ...(props.disabled && {
    pointerEvents: 'none',
    opacity: 0.38,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  }),
  ...(props.nested && {
    paddingLeft: props.withCheckbox ? theme.spacing(3) : theme.spacing(4)
  }),
  ...(props.selected && {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected
    }
  }),
  ...(props.focused && {
    '& .AFSelectOption-group': {
      backgroundColor: '#e7e7e7' // we dont use theme.palette.action.focus due to transparency of fixed group name items
    },
    backgroundColor: theme.palette.action.focus
  }),
  ...(props.lastSelected && {
    borderBottom: `1px solid ${theme.palette.divider}`
  }),
  ...(props.group && {
    padding: theme.spacing(1, 1.5),
    position: 'sticky',
    background: theme.palette.background.paper,
    top: 0,
    zIndex: 10,
    '&:hover': {
      // We can't use palette.action.hover because it has a transparent color which doesn't go well with sticky behavior.
      backgroundColor: 'rgb(247 247 247)'
    }
  })
}));

export const IconContainer = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  minWidth: 'unset',
  paddingRight: theme.spacing(1),
  color: theme.palette.action.active,
  flexShrink: 0,
  [`& .${buttonBaseClasses.root}`]: {
    padding: 7,
    [`&.${checkboxClasses.root}`]: {
      [`& .${svgIconClasses.root}`]: {
        height: 18,
        width: 18
      }
    }
  }
}));

export const LabelContainer = styled('div', {
  shouldForwardProp: prop =>
    'multiline' &&
    prop !== 'group' &&
    prop !== 'isOptionDisabled' &&
    prop !== 'hasAction'
})(({ theme, multiline, group, isOptionDisabled, hasAction }) => ({
  ...theme.typography.body1,
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
  overflow: 'hidden',
  overflowWrap: 'break-word',
  ...(hasAction && {
    flex: 1
  }),
  ...(multiline && {
    marginTop: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75)
  }),
  ...(group && {
    width: '100%',
    margin: 0,
    display: 'flex',
    justifyContent: 'space-between',
    ...(!isOptionDisabled && {
      color: theme.palette.text.secondary,
      [`& .${svgIconClasses.root}`]: {
        color: theme.palette.text.secondary
      }
    })
  })
}));

export const GroupContainer = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary
}));

export const SecondaryTextContainer = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary
}));

export const BadgeContainer = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  margin: theme.spacing(0, 1),
  [`& .${badgeClasses.badge}`]: {
    position: 'relative',
    transform: 'none'
  }
}));
